import { FC } from "react";
import { DragIndicator } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import __ from "src/utils/Translations";
import getFieldTypeLabel from "src/utils/Company/Absences/getFieldTypeLabel";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import LinkifiedText from "src/Pages/Company/Absences/Forms/LinkifiedText";
import {
  FieldType,
  ICustomField,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

interface ICustomFieldPreviewProps {
  field: ICustomField;
  disabled: boolean;
  handleEditField: (fieldId: string) => void;
  handleDeleteField: (fieldId: string) => void;
}

const CustomFieldPreview: FC<ICustomFieldPreviewProps> = ({
  field,
  disabled,
  handleEditField,
  handleDeleteField,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  const isInformationField = field.fieldType === FieldType.INFORMATION;

  return (
    <Stack
      key={field.id}
      ref={setNodeRef}
      direction="row"
      style={style}
      sx={{
        bgcolor: "grey.100",
        padding: "1rem",
        marginBottom: "1rem",
        borderRadius: "0.5rem",
      }}
      data-testid="absence-type-custom-field-preview"
    >
      {!disabled && (
        <IconButton
          {...listeners}
          {...attributes}
          sx={{ cursor: "grab !important" }}
        >
          <DragIndicator />
        </IconButton>
      )}
      <Box sx={{ mt: "0.375rem" }}>
        <Typography variant="subtitle2" component="p" color="primary.dark">
          {getFieldTypeLabel(field.fieldType)}
        </Typography>
        {isInformationField ? (
          <Typography
            variant="subtitle1"
            component="p"
            sx={{ overflowWrap: "anywhere", wordBreak: "normal" }}
          >
            <LinkifiedText text={field.name} />
          </Typography>
        ) : (
          <Typography variant="subtitle1">{field.name}</Typography>
        )}
        {field.description && (
          <Typography
            variant="subtitle2"
            component="p"
            sx={{
              overflowWrap: "anywhere",
              wordBreak: "normal",
            }}
          >
            {field.description}
          </Typography>
        )}
        {field.label && (
          <Typography
            variant="subtitle2"
            component="p"
            sx={{ m: "1rem 0", overflowWrap: "anywhere", wordBreak: "normal" }}
          >
            {field.label}
          </Typography>
        )}
        {field.fieldType === FieldType.CHOICE_SELECTION &&
          field.choices &&
          field.choices.map((choice) => (
            <Typography
              key={choice.id}
              variant="subtitle2"
              component="p"
              sx={{
                m: "1rem 0",
                overflowWrap: "anywhere",
                wordBreak: "normal",
              }}
            >
              {choice.value}
            </Typography>
          ))}

        {!isInformationField && (
          <Typography variant="subtitle2" component="span" sx={{ mr: "1rem" }}>
            • {__("Obowiązkowe")}:{" "}
            <Box component="span" color="primary.dark">
              {field.required ? __("Tak") : __("Nie")}
            </Box>
          </Typography>
        )}
        {field.fieldType === FieldType.CHOICE_SELECTION && (
          <Typography variant="subtitle2" component="span">
            • {__("Wielokrotny wybór")}:{" "}
            <Box component="span" color="primary.dark">
              {field.multipleChoice ? __("Tak") : __("Nie")}
            </Box>
          </Typography>
        )}
      </Box>
      {!disabled && (
        <Stack direction="row" spacing={2} sx={{ ml: "auto" }}>
          <ActionButton
            label={__("Edytuj")}
            bgColor="primary.main"
            bgColorHover="primary.dark"
            color="white"
            onClick={() => handleEditField(field.id)}
            data-testid="absence-type-custom-field__button-edit"
          >
            <EditNoteOutlinedIcon />
          </ActionButton>
          <ActionButton
            label={__("Usuń")}
            bgColor="error.main"
            bgColorHover="error.dark"
            color="white"
            onClick={() => handleDeleteField(field.id)}
            data-testid="absence-type-custom-field__button-delete"
          >
            <DeleteOutline />
          </ActionButton>
        </Stack>
      )}
    </Stack>
  );
};

export default CustomFieldPreview;
